import type { ReactElement } from "react";
import { Link } from "react-router";

import { useApiData } from "../../api/use-api-data.js";
import { usePageMeta } from "../../app/use-page-meta.js";
import { loadFailureMessage } from "../../components/load-failure.js";
import { MessagePage } from "../../components/message-page.js";
import { FormSummary } from "../../forms/fields.js";
import { useApiForm } from "../../forms/use-api-form.js";

import { accountApi, accountUrls } from "./urls.js";
import { accountOf, useSignedInOnly } from "./use-signed-in-only.js";

/** 退会の確認文（<c>GET /api/account/delete</c>。AccountDeleteViewModel）。 */
export interface AccountDeleteConfirmation {
    readonly confirmText: string;
}

/** 最後の確認はブラウザの確認ダイアログで行う（重要な操作は最後にダイアログで確かめる。2026-10-10 に利用者が決めた）。 */
export const deleteAccountDialog = (email: string | undefined): string =>
    `${accountOf(email)}を削除して退会します。この操作は元に戻せません。よろしいですか？`;

/**
 * 退会の確認。<b>確認文はメールのひな型（ConfirmDeleteAccount.txt）をサーバが描いたもの</b>で、
 * 平文として出し、改行は CSS で見せる（HTML にしない）。
 * 契約が残っていれば API が断り、その文を先頭に出す。
 */
export function DeletePage(): ReactElement {
    usePageMeta("退会");
    const data = useApiData<AccountDeleteConfirmation>(accountApi.delete);
    // ダイアログの文に入れるアドレス（確認文の API は返さない）
    const email = useSignedInOnly();
    const form = useApiForm(accountApi.delete, undefined, deleteAccountDialog(email));

    return (
        <div className="page max-w-lg">
            <div className="panel">
                <h1 className="page-title">退会</h1>
                <p className="page-lead">ご確認のうえ、退会の手続きを進めてください。</p>

                <FormSummary messages={form.summary} />

                {data.state === "ready"
                    ? <div className="alert-warning mb-5 whitespace-pre-line" data-confirm-text="">{data.value.confirmText}</div>
                    : null}
                {data.state === "failed"
                    ? <div className="alert-danger mb-5" role="alert">{loadFailureMessage("確認事項", data)}</div>
                    : null}

                <p className="mb-7 text-sm text-slate-500">
                    ご契約中の環境がある場合は、先にすべて解約してください。
                </p>

                <div className="flex gap-3">
                    <Link to={accountUrls.settings} className="btn-secondary grow">キャンセル</Link>
                    <form method="post" noValidate onSubmit={form.onSubmit} className="grow">
                        {/* 確認文を読めるまでは押させない（読まずに退会させない） */}
                        <button type="submit" className="btn-danger w-full" disabled={form.busy || data.state !== "ready"}>
                            退会する
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}

export function DeleteCompletedPage(): ReactElement {
    return (
        <MessagePage
            title="退会が完了しました"
            actions={<a href={accountUrls.home} className="btn-secondary">トップへ</a>}
        >
            ご利用いただきありがとうございました。<br />退会完了のお知らせをお送りしています。
        </MessagePage>
    );
}
